/**
 * PostHog event capture — Keystone customer sites.
 *
 * ## Naming convention
 * All events use snake_case `object_action` format.
 * Properties use snake_case as well.
 *
 * ## Event taxonomy
 * Add new events here: define the name in `KsEventName` and its required
 * properties in `KsEventProperties`. Every callsite is then type-checked.
 *
 * ## Usage
 *
 *   import { captureEvent } from 'keystone-design-bootstrap/tracking';
 *
 *   captureEvent('form_submitted', { form_type: 'lead' });
 *   captureEvent('booking_cta_clicked', { source_path: '/services/massage', booking_url: url });
 *
 * All calls are safe no-ops when PostHog has not been initialised (e.g. no
 * POSTHOG_API_KEY configured on the server).
 *
 * ## Super properties
 * account_id, account_name, and site_domain are registered as super properties
 * by PostHogProvider and are automatically attached to every event — you do not
 * need to include them in individual captureEvent calls.
 */

import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// Event taxonomy
// ---------------------------------------------------------------------------

export type KsEventName =
  // Booking / conversion
  | 'booking_cta_clicked'
  // Forms
  | 'form_submitted'
  | 'form_failed'
  // Chat widget
  | 'chat_opened'
  | 'chat_message_sent'
  | 'chat_message_failed'
  // Member portal — tab navigation
  | 'portal_tab_viewed'
  // Member portal — authentication flow
  | 'portal_login_started'
  | 'portal_login_step_viewed'
  | 'portal_login_step_submitted'
  | 'portal_login_step_advanced'
  | 'portal_login_back_clicked'
  | 'portal_login_identified'
  | 'portal_login_completed'
  | 'portal_login_failed';

export type KsEventProperties = {
  /** Fired when a visitor clicks any CTA that links to the external booking URL. */
  booking_cta_clicked: {
    source_path: string;
    booking_url: string;
  };

  /** Fired when a Keystone form is successfully submitted. */
  form_submitted: {
    /** One of: lead | job_application | marketing_list_signup */
    form_type: string;
    /** Server-generated event ID for CAPI deduplication (when present). */
    event_id?: string;
  };

  /** Fired when a form submission fails (validation error or network error). */
  form_failed: {
    form_type: string;
    error: string;
  };

  /** Fired when the chat widget is first opened by the visitor. */
  chat_opened: Record<string, never>;

  /** Fired when a chat message is successfully sent. */
  chat_message_sent: {
    /** Whether the visitor is authenticated (contactId present). */
    is_authenticated: boolean;
  };

  /** Fired when a chat message fails to send. */
  chat_message_failed: {
    error: string;
  };

  /** Fired when a member portal tab is opened. */
  portal_tab_viewed: {
    tab: string;
  };

  /** Fired when the portal login modal is opened / login flow starts. */
  portal_login_started: Record<string, never>;

  /** Fired whenever a login step becomes visible to the user. */
  portal_login_step_viewed: {
    step: 'identifier' | 'signin' | 'signup';
  };

  /** Fired when the user submits a specific login step. */
  portal_login_step_submitted: {
    step: 'identifier' | 'signin' | 'signup';
  };

  /** Fired when the flow transitions from one step to another state. */
  portal_login_step_advanced: {
    from_step: 'identifier' | 'signin' | 'signup';
    to_step: 'signin' | 'signup' | 'authenticated';
    reason: string;
  };

  /** Fired when the user explicitly navigates back to the identifier step. */
  portal_login_back_clicked: {
    from_step: 'signin' | 'signup';
    to_step: 'identifier';
  };

  /**
   * Fired after the identifier step resolves — we know whether the user
   * already has an account.
   */
  portal_login_identified: {
    method: 'email' | 'phone' | 'email_and_phone';
    user_exists: boolean;
  };

  /** Fired after the user successfully signs in or creates an account. */
  portal_login_completed: {
    flow: 'signin' | 'signup';
  };

  /** Fired when any step of the login flow returns an error. */
  portal_login_failed: {
    step: 'identifier' | 'signin' | 'signup';
    reason: string;
  };
};

// ---------------------------------------------------------------------------
// Capture helper
// ---------------------------------------------------------------------------

/**
 * Captures a typed Keystone analytics event via PostHog.
 * Safe no-op when PostHog has not been initialised.
 */
export function captureEvent<E extends KsEventName>(
  event: E,
  ...args: KsEventProperties[E] extends Record<string, never>
    ? []
    : [properties: KsEventProperties[E]]
): void {
  posthog.capture(event, args[0] as Record<string, unknown>);
}

/**
 * Captures an app-specific ("custom") analytics event via PostHog.
 *
 * Unlike `captureEvent` — which is restricted to the shared, type-checked
 * Keystone taxonomy (`KsEventName`) — this is a thin, open-ended escape hatch
 * for product-specific funnels that don't belong in the shared taxonomy
 * (e.g. the Grader's `grader_scan_started`, or any one-off consumer app event).
 * It keeps those events out of the shared union while still flowing through the
 * same initialised PostHog instance, so account super properties and config are
 * applied identically.
 *
 * Conventions (not enforced, but please follow):
 * - snake_case `object_action` names, prefixed with your app/feature so events
 *   are easy to group and never collide with the shared taxonomy
 *   (e.g. `grader_scan_started`; `portal_*`, `form_*`, `chat_*`, `booking_*`
 *   are reserved by `captureEvent`).
 * - snake_case property keys.
 *
 * `account_id` / `account_name` / `site_domain` super properties are attached
 * automatically by PostHogProvider. Safe no-op when PostHog isn't initialised.
 *
 * For events that are reused across multiple sites, promote them into the typed
 * `KsEventName` taxonomy above and use `captureEvent` instead.
 */
export function captureCustomEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  posthog.capture(event, properties);
}
